import { Button, Grid } from '@mui/material'
import Select from 'components/forms/Select'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { intersectionWith, isNil, uniq, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import DtoOperation from 'quality/dto/operation/DtoOperation'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import { H_HYDRO_MODULE, H_PIEZO_MODULE, H_PLUVIO_MODULE } from '../../../../account/constants/AccessRulesConstants'
import Checkbox from '../../../../components/forms/Checkbox'
import NumberField from '../../../../components/forms/NumberField'
import RadioButtons from '../../../../components/forms/RadioButtons'
import SimpleDatePicker from '../../../../components/forms/SimpleDatePicker'
import SelectionSelect from '../../../../components/forms/specific/SelectionSelect'
import ThresholdSelect from '../../../../components/forms/specific/ThresholdSelect'
import Icon from '../../../../components/icon/Icon'
import CollapseTopBar from '../../../../components/topBar/CollapseToBar'
import HabilitationRequired from '../../../../utils/components/HabilitationRequired'
import { hasValue } from '../../../../utils/NumberUtil'
import { getHardPiezoDataTypes } from '../../../../utils/PiezometryUtils'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../../utils/StatusUtil'
import { i18nize } from '../../../../utils/StringUtil'
import { HORIZON_LIST, REGROUPING_LIST } from './constants/SuiviPCConstants'
import { getHardHydroDataTypes } from 'utils/HydroUtils'
import useListIndexed from 'utils/customHook/useListIndexed'
import useLocalStorage from '../../../../utils/customHook/useLocalStorage'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { darkBlue, lightGreyBlue } from 'utils/constants/ColorTheme'

const FieldsetHydro = ({
    hydroDatas = [{}],
    setHydroDatas = () => {},
}) => {
    const {
        hydrometricStations,
        hydrometryDataTypes,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    const dataTypes = useMemo(() => uniqBy([...hydrometryDataTypes, ...getHardHydroDataTypes()], 'id'), [hydrometryDataTypes])

    const onChangeHydro = (obj, index) => setHydroDatas(datas => datas.map((hydroData, i) => index === i ? { ...hydroData, ...obj } : hydroData))

    return (
        <HabilitationRequired habilitation={H_HYDRO_MODULE}>
            <fieldset>
                <legend>
                    {hydroDatas.length > 1 ? i18n.hydrometricStations : i18n.hydrometricStation}
                </legend>
                {
                    hydroDatas.map((hydroData, index) => {
                        return (
                            <Grid key={hydroData.id} container spacing={2} columns={24}>
                                <Grid size={8}>
                                    <SuperMultiAutocomplete
                                        label={i18n.nameOfStation}
                                        options={hydrometricStations}
                                        values={hydroData.id}
                                        onChange={id => onChangeHydro({ id }, index)}
                                    />
                                </Grid>
                                <Grid size={8}>
                                    <SuperMultiAutocomplete
                                        options={dataTypes}
                                        values={hydroData.type}
                                        label={i18n.chronicType}
                                        keyLabel='label'
                                        onChange={type => onChangeHydro({ type }, index)}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    {
                                        hasValue(hydroData.id) && hydroData.type === 5 && (
                                            <Checkbox
                                                label={i18n.calculateFlux}
                                                checked={hydroData.calculateFlux}
                                                componentClassName={'padding-top-1'}
                                                onChange={calculateFlux => onChangeHydro({ calculateFlux }, index)}
                                            />
                                        )
                                    }
                                </Grid>
                                <Grid size={3}>
                                    {
                                        hasValue(hydroData.id) && hydroData.type === 5 && hydroData.calculateFlux && (
                                            <NumberField
                                                title={i18n.coeff}
                                                value={hydroData.coeffFlux}
                                                floatValue
                                                onChange={coeffFlux => onChangeHydro({ coeffFlux }, index)}
                                            />
                                        )
                                    }
                                </Grid>
                                <Grid size={1}>
                                    {
                                        hydroDatas.length - 1 === index && (
                                            <Icon
                                                icon={'add_circle'}
                                                onClick={() => setHydroDatas(datas => [...datas, {}])}
                                                className={'col s2 offset-s1'}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                tooltip={i18n.addStation}
                                            />
                                        )
                                    }
                                </Grid>
                                <Grid size={1}>
                                    {
                                        hydroDatas.length > 1 && (
                                            <Icon
                                                icon={'remove_circle'}
                                                onClick={() => setHydroDatas(datas => datas.filter((_, i) => index !== i))}
                                                tooltip={i18n.deleteStation}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                className={'col s2 offset-s1'}
                                            />
                                        )
                                    }
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </fieldset>
        </HabilitationRequired>
    )
}

FieldsetHydro.propTypes = {
    hydroDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        calculateFlux: PropTypes.bool,
        coeffFlux: PropTypes.number,
    })),
    setHydroDatas: PropTypes.func,
}

const FieldsetPiezo = ({
    piezoDatas = [{}],
    setPiezoDatas = () => { },
}) => {
    const {
        piezometers,
        piezometryDataTypes,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
    }), shallowEqual)

    const chronicTypes = useMemo(() => [
        { id: 'piezometryChronic', label: i18n.chronic },
        ...getHardPiezoDataTypes(),
        ...piezometryDataTypes,
    ], [piezometryDataTypes])

    const onChangePiezo = (obj, index) => setPiezoDatas(datas => datas.map((piezoData, i) => index === i ? { ...piezoData, ...obj } : piezoData))

    return (
        <HabilitationRequired habilitation={H_PIEZO_MODULE}>
            <fieldset>
                <legend>
                    {piezometers.length > 1 ? i18n.piezometers : i18n.piezometer}
                </legend>
                {
                    piezoDatas.map((piezoData, index) => {
                        return (
                            <Grid key={piezoData.id} container spacing={2} columns={24}>
                                <Grid size={8}>
                                    <SuperMultiAutocomplete
                                        label={i18n.nameOfStation}
                                        options={piezometers.map(({ id, code, name }) => ({ id, name: name && `${name} [${code}]` || code }))}
                                        values={piezoData.id}
                                        onChange={id => onChangePiezo({ id }, index)}
                                    />
                                </Grid>
                                <Grid size={8}>
                                    <SuperMultiAutocomplete
                                        options={chronicTypes}
                                        values={piezoData.type}
                                        label={i18n.chronicType}
                                        keyLabel='label'
                                        onChange={type => onChangePiezo({ type }, index)}
                                    />
                                </Grid>
                                <Grid size={6} />
                                <Grid size={1}>
                                    {
                                        piezoDatas.length - 1 === index && (
                                            <Icon
                                                icon={'add_circle'}
                                                onClick={() => setPiezoDatas(datas => [...datas, {}])}
                                                className={'col s2 offset-s1'}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                tooltip={i18n.addStation}
                                            />
                                        )
                                    }
                                </Grid>
                                <Grid size={1}>
                                    {
                                        piezoDatas.length > 1 && (
                                            <Icon
                                                icon={'remove_circle'}
                                                onClick={() => setPiezoDatas(datas => datas.filter((_, i) => index !== i))}
                                                tooltip={i18n.deleteStation}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                className={'col s2 offset-s1'}
                                            />
                                        )
                                    }
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </fieldset>
        </HabilitationRequired>
    )
}

FieldsetPiezo.propTypes = {
    piezoDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    })),
    setPiezoDatas: PropTypes.func,
}

const FieldsetPluvio = ({
    pluvioDatas = [{ cumul: 1 }],
    setPluvioDatas = () => { },
}) => {
    const {
        pluviometers,
        pluviometryDataTypes,
    } = useSelector(store => ({
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
    }), shallowEqual)

    const onChangePluvio = (obj, index) => setPluvioDatas(datas => datas.map((pluvioData, i) => index === i ? { ...pluvioData, ...obj } : pluvioData))

    return (
        <HabilitationRequired habilitation={H_PLUVIO_MODULE}>
            <fieldset>
                <legend>
                    {pluviometers.length > 1 ? i18n.pluviometers : i18n.pluviometer}
                </legend>
                {
                    pluvioDatas.map((pluvioData, index) => {
                        return (
                            <Grid key={pluvioData.id} container spacing={2} columns={24}>
                                <Grid size={8}>
                                    <SuperMultiAutocomplete
                                        label={i18n.nameOfStation}
                                        options={pluviometers}
                                        values={pluvioData.id}
                                        onChange={id => onChangePluvio({ id }, index)}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <SuperMultiAutocomplete
                                        options={pluviometryDataTypes}
                                        values={pluvioData.type}
                                        label={i18n.chronicType}
                                        keyLabel='label'
                                        onChange={type => onChangePluvio({ type }, index)}
                                    />

                                </Grid>
                                <Grid size={4}>
                                    <NumberField
                                        value={pluvioData.offset}
                                        title={i18n.offsetInDays}
                                        onChange={offset => onChangePluvio({ offset }, index)}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <NumberField
                                        value={pluvioData.cumul}
                                        title={i18n.cumulativeInDay}
                                        onChange={cumul => onChangePluvio({ cumul: cumul ?? 1 }, index)}
                                    />
                                </Grid>
                                <Grid size={2} />
                                <Grid size={1}>
                                    {
                                        pluvioDatas.length - 1 === index && (
                                            <Icon
                                                icon={'add_circle'}
                                                onClick={() => setPluvioDatas(datas => [...datas, { cumul: 1 }])}
                                                className={'col s2 offset-s1'}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                tooltip={i18n.addStation}
                                            />
                                        )
                                    }
                                </Grid>
                                <Grid size={1}>
                                    {
                                        pluvioDatas.length > 1 && (
                                            <Icon
                                                icon={'remove_circle'}
                                                onClick={() => setPluvioDatas(datas => datas.filter((_, i) => index !== i))}
                                                tooltip={i18n.deleteStation}
                                                style={{ fontSize: '3rem', color: '35609f' }}
                                                className={'col s2 offset-s1'}
                                            />
                                        )
                                    }
                                </Grid>
                            </Grid>
                        )
                    })
                }
            </fieldset>
        </HabilitationRequired>
    )
}

FieldsetPluvio.propTypes = {
    pluvioDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        offset: PropTypes.number,
        cumul: PropTypes.number,
    })),
    setPluvioDatas: PropTypes.func,
}

const SuiviPCcriteriaPanel = ({
    onValidate = () => { },
    analysis = [],
    operations = [],
    defaultFilter = {},
}) => {
    const {
        qualitometer,
        qualityCampaignStations,
        qualityCampaigns,
        parametersProps,
        parameterGroupUsage,
        contributors,
        supports,
        fractions,
        points,
    } = useSelector(store => ({
        qualitometer: store.QualityReducer.qualitometer,
        qualityCampaignStations: store.QualityReducer.qualityCampaignStations,
        qualityCampaigns: store.QualityReducer.qualityCampaigns,
        parametersProps: store.ParameterReducer.parameters,
        parameterGroupUsage: store.ParameterReducer.parameterGroupUsage,
        qualitometers: store.QualityReducer.qualitometersLight,
        contributors: store.ContributorReducer.contributors,
        supports: store.SupportReducer.supports,
        fractions: store.FractionReducer.fractions,
        points: store.QualityReducer.points,
    }), shallowEqual)

    const indexedContributors = useListIndexed(contributors, 'id')

    const [startDate, setStartDate] = useLocalStorage('START_DATE_SUIVI_PC', defaultFilter.startDate)
    const [endDate, setEndDate] = useLocalStorage('END_DATE_SUIVI_PC', defaultFilter.endDate)
    const [parameters, setParameters] = useLocalStorage('PARAMETERS_SUIVI_PC', defaultFilter.parameters)
    const [selection, setSelection] = useLocalStorage('SELECTION_SUIVI_PC', defaultFilter.selection)
    const [selectionResults, setSelectionResults] = useLocalStorage('SELECTION_RESULTS_SUIVI_PC', defaultFilter.selectionResults)
    const [group, setGroup] = useLocalStorage('GROUP_SUIVI_PC', defaultFilter.group)
    const [threshold, setThreshold] = useLocalStorage('THRESHOLD_SUIVI_PC', defaultFilter.threshold)

    const [producers, setProducers] = useLocalStorage('PRODUCERS_SUIVI_PC', defaultFilter.producers)
    const [laboratories, setLaboratories] = useLocalStorage('LABORATORIES_SUIVI_PC', defaultFilter.laboratories)
    const [point, setPoint] = useLocalStorage('POINT_SUIVI_PC', defaultFilter.point)
    const [campaign, setCampaign] = useLocalStorage('CAMPAIGN_SUIVI_PC', defaultFilter.campaign)
    const [support, setSupport] = useLocalStorage('SUPPORT_SUIVI_PC', defaultFilter.support)
    const [fraction, setFraction] = useLocalStorage('FRACTION_SUIVI_PC', defaultFilter.fraction)
    const [status, setStatus] = useLocalStorage('STATUT_SUIVI_PC', defaultFilter.status)
    const [qualification, setQualification] = useLocalStorage('QUALIFICATION_SUIVI_PC', defaultFilter.qualification)

    const [quantificationControl, setQuantificationControl] = useLocalStorage('QUANTIFICATION_CONTROL_SUIVI_PC', defaultFilter.quantificationControl)
    const [horizon, setHorizon] = useLocalStorage('HORIZON_SUIVI_PC', defaultFilter.horizon)
    const [regrouping, setRegrouping] = useLocalStorage('REGROUPING_SUIVI_PC', defaultFilter.regrouping)
    const [detectionControl, setDetectionControl] = useLocalStorage('DETECTION_CONTROL_SUIVI_PC', defaultFilter.detectionControl)
    const [displayInListMode, setDisplayInListMode] = useLocalStorage('DISPLAY_LIST_MODE_SUIVI_PC', defaultFilter.displayInListMode)
    const [displayAssociatedStations, setDisplayAssociatedStations] = useLocalStorage('DISPLAY_ASSOCIATED_STATIONS_SUIVI_PC', defaultFilter.displayAssociatedStations)
    const [displayAdvancedStatistics, setDisplayAdvancedStatistics] = useLocalStorage('DISPLAY_ADV_STATS_SUIVI_PC', defaultFilter.displayAdvancedStatistics)
    const [groupEquivalences, setGroupEquivalences] = useLocalStorage('GROUP_EQUIVALENCES_SUIVI_PC', defaultFilter.groupEquivalences)

    const [hydroDatas, setHydroDatas] = useLocalStorage('HYDRO_DATAS_SUIVI_PC', defaultFilter.hydroDatas)
    const [piezoDatas, setPiezoDatas] = useLocalStorage('PIEZO_DATAS_SUIVI_PC', defaultFilter.piezoDatas)
    const [pluvioDatas, setPluvioDatas] = useLocalStorage('PLUVIO_DATAS_SUIVI_PC', defaultFilter.pluvioDatas)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const statusList = useMemo(getStatusSelectOptions, [])

    const qualificationList = useMemo(getQualificationSelectOptions, [])

    const parameterList = useMemo(() => {
        const uniqParams = uniqBy(analysis, 'parameter').map(a => a.parameter)
        const selectionParams = selection !== '-1' ? intersectionWith(parametersProps, selectionResults, (p, code) => code === p.code) : parametersProps
        return intersectionWith(selectionParams, uniqParams, (p, code) => p.code === code)
    }, [analysis, parametersProps, selection, selectionResults])

    const groupList = useMemo(() => {
        const filteredGroupUsage = parameterGroupUsage.filter(pgu => parameterList.some(p => p.code === pgu.parameter))
        return uniqBy(filteredGroupUsage, 'groupCode')
    }, [parameterGroupUsage, parameterList])

    const supportList = useMemo(() => {
        const uniqSupports = uniqBy(analysis, 'support').map(a => a.support)
        return intersectionWith(supports, uniqSupports, (p, code) => p.code === `${code}`)
    }, [analysis, supports])

    const fractionList = useMemo(() => {
        const uniqFractions = uniqBy(analysis, 'fraction').map(a => a.fraction)
        return intersectionWith(fractions, uniqFractions, (p, code) => p.id === code)
    }, [analysis, fractions])

    const producerList = useMemo(() => {
        const uniqByProducer = uniqBy(operations, 'producer')
        return uniqByProducer.map(op => {
            const cont = indexedContributors[op.producer]
            if (isNil(cont)) return cont
            return { id: op.producer, name: cont.labelDisplay }
        }).filter(s => !isNil(s))
    }, [operations, indexedContributors])

    const laboList = useMemo(() => {
        const uniqByProducer = uniqBy(analysis, 'labo')
        return uniqByProducer.map(o => {
            const cont = indexedContributors[o.labo]
            if (isNil(cont)) return cont
            return { id: o.labo, name: cont.labelDisplay }
        }).filter(s => !isNil(s))
    }, [analysis, indexedContributors])

    const campaignCodes = qualityCampaignStations.filter(cs => cs.stationId === qualitometer.id).map(cs => cs.campaignId)
    const filteredCampaigns = qualityCampaigns.filter(cs => cs.campaignType === 3)
    const campaigns = uniq(campaignCodes).map(cCode => filteredCampaigns.find(c => c.id === cCode)).filter(c => !!c)

    useEffect(() => {
        onValidate({
            startDate,
            endDate,
            parameters,
            selection,
            selectionResults,
            group,
            threshold,

            producers,
            laboratories,
            point,
            campaign,
            support,
            fraction,
            status,
            qualification,

            quantificationControl,
            horizon,
            regrouping,
            detectionControl,
            displayInListMode,
            displayAssociatedStations,
            displayAdvancedStatistics,
            groupEquivalences,

            hydroDatas,
            piezoDatas,
            pluvioDatas,
        })
    }, [])

    return (
        <div style={{ margin: '0 20px 10px 20px' }}>
            <div style={{ backgroundColor: lightGreyBlue, borderLeft: `2px solid ${darkBlue}`, borderRight: `2px solid ${darkBlue}`, padding: '10px 0 5px 0' }}>
                <Grid container columnSpacing={2} rowSpacing={1} sx={{ padding: '0 10' }}>
                    <Grid size={2}>
                        <SimpleDatePicker
                            id='startDate'
                            label={i18n.startDate}
                            value={startDate}
                            onChange={setStartDate}
                            max={endDate}
                        />
                    </Grid>
                    <Grid size={2}>
                        <SimpleDatePicker
                            id='endDate'
                            label={i18n.endDate}
                            value={endDate}
                            onChange={setEndDate}
                            min={startDate}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SelectionSelect
                            value={selection}
                            onChange={(results, value) => {
                                setSelection(value)
                                setSelectionResults(results)
                            }}
                        />
                    </Grid>
                    <Grid size={2}>
                        <SuperMultiAutocomplete
                            label={i18n.parameters}
                            options={parameterList}
                            values={parameters}
                            onChange={setParameters}
                            keyLabel={showSandreCode ? 'displayLabel' : 'displayName'}
                            keyValue='code'
                            additionalKeysFilter={['name', 'shortLabel', 'code']}
                            multiple
                        />
                    </Grid>
                    <Grid size={2}>
                        <SuperMultiAutocomplete
                            label={i18n.group}
                            options={groupList}
                            values={group}
                            onChange={setGroup}
                            keyValue='groupCode'
                            keyLabel='groupName'
                        />
                    </Grid>
                    <Grid size={4}>
                        <ThresholdSelect
                            selected={threshold}
                            onChange={setThreshold}
                            nullLabel='&nbsp;'
                        />
                    </Grid>
                    <Grid size={4} />
                    <Grid size={4}>
                        <Button
                            onClick={() => onValidate({
                                startDate,
                                endDate,
                                parameters,
                                selection,
                                selectionResults,
                                group,
                                threshold,

                                producers,
                                laboratories,
                                point,
                                campaign,
                                support,
                                fraction,
                                status,
                                qualification,

                                quantificationControl,
                                horizon,
                                regrouping,
                                detectionControl,
                                displayInListMode,
                                displayAssociatedStations,
                                displayAdvancedStatistics,
                                groupEquivalences,

                                hydroDatas,
                                piezoDatas,
                                pluvioDatas,
                            })}
                            variant='contained'
                            fullWidth
                        >
                            {i18n.visualize}
                        </Button>
                    </Grid>
                </Grid>
            </div>
            <CollapseTopBar>
                <div style={{ backgroundColor: lightGreyBlue, borderLeft: `2px solid ${darkBlue}`, borderRight: `2px solid ${darkBlue}` }}>
                    <Grid container columnSpacing={2} rowSpacing={1} sx={{ padding: '2 10 10 10' }}>
                        <Grid size={3}>
                            <MultiContributorsAutocomplete
                                options={producerList}
                                label={i18n.producer}
                                onChange={setProducers}
                                multiple
                            />
                        </Grid>
                        <Grid size={3}>
                            <MultiContributorsAutocomplete
                                options={laboList}
                                label={i18n.laboratory}
                                onChange={setLaboratories}
                                multiple
                            />
                        </Grid>
                        <Grid size={3}>
                            <SuperMultiAutocomplete
                                options={points}
                                values={point}
                                label={i18n.samplePoint}
                                keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                                additionalKeysFilter={['code']}
                                keyValue='idPoint'
                                onChange={setPoint}
                            />
                        </Grid>
                        <Grid size={3}>
                            <SuperMultiAutocomplete
                                options={campaigns}
                                values={campaign}
                                label={i18n.campaign}
                                onChange={setCampaign}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.support}
                                options={supportList}
                                value={support}
                                onChange={setSupport}
                                keyValue='id'
                                clearFunction
                                displayWithCode={showSandreCode}
                            />
                        </Grid>
                        <Grid size={3}>
                            <Select
                                label={i18n.fraction}
                                options={fractionList}
                                value={fraction}
                                onChange={setFraction}
                                keyValue='id'
                                clearFunction
                                displayWithCode={showSandreCode}
                            />
                        </Grid>
                        <Grid size={3}>
                            <SuperMultiAutocomplete
                                options={statusList}
                                values={status}
                                label={i18n.status}
                                keyLabel='name'
                                keyValue='id'
                                onChange={setStatus}
                                multiple
                            />
                        </Grid>
                        <Grid size={3}>
                            <SuperMultiAutocomplete
                                label={i18n.qualification}
                                options={qualificationList}
                                values={qualification}
                                keyLabel='name'
                                keyValue='id'
                                onChange={setQualification}
                                multiple
                            />
                        </Grid>
                        <Grid size={12}>
                            <FieldsetHydro
                                hydroDatas={hydroDatas}
                                setHydroDatas={setHydroDatas}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FieldsetPiezo
                                piezoDatas={piezoDatas}
                                setPiezoDatas={setPiezoDatas}
                            />
                        </Grid>
                        <Grid size={12}>
                            <FieldsetPluvio
                                pluvioDatas={pluvioDatas}
                                setPluvioDatas={setPluvioDatas}
                            />
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.quantificationControl}
                                checked={quantificationControl}
                                onChange={setQuantificationControl}
                            />
                        </Grid>
                        <Grid size={4}>
                            <RadioButtons
                                title={i18n.horizon}
                                elements={i18nize(HORIZON_LIST)}
                                selected={horizon}
                                onChange={setHorizon}
                            />
                        </Grid>
                        <Grid size={4}>
                            {
                                horizon !== 'operations' && (
                                    <RadioButtons
                                        elements={i18nize(REGROUPING_LIST)}
                                        title={i18n.regrouping}
                                        selected={regrouping}
                                        onChange={setRegrouping}
                                    />
                                )
                            }
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.detectionControl}
                                checked={detectionControl}
                                onChange={setDetectionControl}
                            />
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.displayInListMode}
                                checked={displayInListMode}
                                onChange={setDisplayInListMode}
                            />
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.displayAssociatedStations}
                                checked={displayAssociatedStations}
                                onChange={setDisplayAssociatedStations}
                            />
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.displayAdvancedStatistics}
                                checked={displayAdvancedStatistics}
                                onChange={setDisplayAdvancedStatistics}
                            />
                        </Grid>
                        <Grid size={4} sx={{ height: '46px' }}>
                            <Checkbox
                                label={i18n.groupEquivalences}
                                checked={groupEquivalences}
                                onChange={setGroupEquivalences}
                            />
                        </Grid>
                    </Grid>
                </div>
            </CollapseTopBar>
        </div>
    )
}

SuiviPCcriteriaPanel.propTypes = {
    onValidate: PropTypes.func,
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    operations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
    defaultFilter: PropTypes.shape({/* see parent component */}),
}

export default SuiviPCcriteriaPanel
